import Select, {
  ActionMeta,
  CSSObjectWithLabel,
  GroupBase,
  OptionProps,
  SingleValue,
} from 'react-select';
import { useTheme } from '../hooks/theme';
import CheckMark from 'assets/svgs/checkMark.svg';

export enum DropdownType {
  body = 'body',
  heading = 'heading',
}

export type Option = {
  value: string;
  label: string;
  component?: JSX.Element;
};

type DropdownProps = {
  onChange: (
    newValue: SingleValue<Option>,
    actionMeta: ActionMeta<Option>,
  ) => void;
  options: Option[];
  value?: string;
  id?: string;
  inputId?: string;
  type?: DropdownType;
  isSearchable?: boolean;
  placeholder?: string;
  headingSize?: 'sm';
};

const CustomOption = ({
  innerProps,
  isSelected,
  data,
}: OptionProps<Option, false, GroupBase<Option>>): JSX.Element | null => (
  <div
    {...innerProps}
    id={data.value}
    sx={{
      fontFamily: isSelected ? 'heading' : 'body',
      margin: '3px 12px',
      borderRadius: 1,
      display: 'flex',
      padding: isSelected ? '6px 12px' : '6px 10px 6px 20px',
      '&:hover': {
        backgroundColor: 'backgroundWeak',
        cursor: 'pointer',
      },
      justifyContent: 'space-between',
      alignItems: 'center',
    }}
  >
    <div>
      {isSelected && (
        <span sx={{ paddingRight: '5px' }}>
          <CheckMark />
        </span>
      )}{' '}
      {data.label}
    </div>
    <div>{data.component}</div>
  </div>
);

const Dropdown = ({
  onChange,
  value,
  id,
  inputId,
  options,
  type = DropdownType.body,
  isSearchable = false,
  placeholder,
  headingSize,
}: DropdownProps): JSX.Element => {
  let selectedOption = options.find((option) => option.value === value);

  const {
    theme: {
      fonts: { body, heading },
      colors: {
        text,
        secondaryBackground,
        secondaryBackgroundActive,
        backgroundMedium,
        mocha,
      },
      text: { headingSm },
    },
  } = useTheme();
  const isSmallHeading = type === DropdownType.heading && headingSize === 'sm';

  const controlHeading = ({
    isFocused,
  }: {
    isFocused: boolean;
  }): CSSObjectWithLabel => ({
    fontSize: isSmallHeading ? headingSm.fontSize : '24px',
    lineHeight: isSmallHeading ? headingSm.lineHeight : undefined,
    minHeight: isSmallHeading ? 'auto' : undefined,
    borderStyle: 'none',
    boxShadow: isFocused ? `0 0 0 1px ${backgroundMedium}` : 'none',
  });

  const controlBody = ({
    isFocused,
  }: {
    isFocused: boolean;
  }): CSSObjectWithLabel => ({
    fontSize: '14px',
    borderStyle: 'solid',
    boxShadow: 'none',
    borderColor: isFocused
      ? `${secondaryBackgroundActive}`
      : `${backgroundMedium}`,
    '&:hover': {
      borderColor: `${secondaryBackgroundActive}`,
    },
  });

  const control = ({ isFocused }: { isFocused: boolean }): CSSObjectWithLabel =>
    type === DropdownType.heading
      ? controlHeading({ isFocused })
      : controlBody({ isFocused });

  const customStyles = {
    dropdownIndicator: () => ({
      color: isSmallHeading ? mocha : text,
      padding: isSmallHeading
        ? '4px'
        : type === DropdownType.heading
        ? '14px 30px 10px 0px'
        : '10px 10px 6px 10px',
      svg: isSmallHeading ? { height: '16px', width: '16px' } : undefined,
    }),
    control: (
      provided: CSSObjectWithLabel,
      { isFocused }: { isFocused: boolean },
    ) => ({
      ...provided,
      cursor: 'pointer',
      fontFamily: body,
      ...control({ isFocused }),
    }),
    singleValue: (provided: CSSObjectWithLabel) => ({
      ...provided,
      color: isSmallHeading ? mocha : provided.color,
      fontFamily: type === DropdownType.heading ? heading : body,
      paddingRight: type === DropdownType.heading ? '0px' : '30px',
    }),
    valueContainer: (provided: CSSObjectWithLabel) => ({
      ...provided,
      padding: isSmallHeading ? 0 : provided.padding,
    }),
    input: (provided: CSSObjectWithLabel) => ({
      ...provided,
      margin: isSmallHeading ? 0 : provided.margin,
      padding: isSmallHeading ? 0 : provided.padding,
    }),
    indicatorSeparator: () => ({
      display: 'none',
    }),
    menu: (provided: CSSObjectWithLabel) => ({
      ...provided,
      padding: '10px 0px',
      maxHeight: '236px',
      zIndex: 150,
      minWidth: '130px',
    }),
    menuList: (provided: CSSObjectWithLabel) => ({
      ...provided,
      maxHeight: '216px',
      scroll: 'auto',
    }),
    placeholder: (provided: CSSObjectWithLabel) => ({
      ...provided,
      color: isSmallHeading ? mocha : 'black',
      fontFamily: type === DropdownType.heading ? heading : body,
    }),
    noOptionsMessage: (provided: CSSObjectWithLabel) => ({
      ...provided,
      fontFamily: body,
    }),
  };
  return (
    <Select
      id={id}
      inputId={inputId}
      onChange={onChange}
      options={options}
      value={selectedOption}
      styles={customStyles}
      isSearchable={isSearchable}
      components={{ Option: CustomOption }}
      placeholder={placeholder}
    />
  );
};

export default Dropdown;
